import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Autocomplete, Box, Chip, Grid, Link, Stack, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import _ from "lodash";

import { AppContext } from "../../App";
import { formatDate, formatPrice } from "../../utility/formatters";
import { getBudgetYears } from "../Budget/helpers";
import DropdownSelector from "../DropdownSelector";
import { DEBT_SCHEDULE_CATEGORIES, DEBT_SCHEDULE_PAYMENT_FREQUENCIES, DEBT_SCHEDULE_PURPOSE_LIST } from "./helpers";
import useAllEquipmentPayments from "./hooks/useAllEquipmentPayments";

/**
 * All Equipment Payments Grid Component
 * Displays a filterable data grid showing all debt schedule amortization payments
 */
export const AllEquipmentPaymentsGrid = ({ onFilteredDataChange }) => {
  const { globalState } = useContext(AppContext);
  const { payments, isLoading } = useAllEquipmentPayments(globalState);
  const navigate = useNavigate();

  // Filter state
  const [filterYear, setFilterYear] = useState(null); // null means "All Years"
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterPurposes, setFilterPurposes] = useState([]);
  const [filterEquipment, setFilterEquipment] = useState([]);
  const [filterCreditors, setFilterCreditors] = useState([]);
  const [filterPaymentFrequencies, setFilterPaymentFrequencies] = useState([]);

  const availableCategories = DEBT_SCHEDULE_CATEGORIES;

  const availablePurposes = DEBT_SCHEDULE_PURPOSE_LIST;

  const availableEquipment = useMemo(() => {
    if (!payments || payments.length === 0) return [];
    const equipmentList = _.uniqBy(
      payments.map((p) => ({
        name: p.equipment_name,
        category: p.equipment_category,
      })),
      "name"
    ).filter((e) => e.name && e.name !== "Unknown");
    return _.sortBy(equipmentList, "name");
  }, [payments]);

  const availableCreditors = useMemo(() => {
    if (!payments || payments.length === 0) return [];
    return _.uniq(payments.map((p) => p.creditor).filter(Boolean)).sort();
  }, [payments]);

  const availablePaymentFrequencies = DEBT_SCHEDULE_PAYMENT_FREQUENCIES;

  // Filter payments based on selected filters
  const filteredPayments = useMemo(() => {
    if (!payments) return [];

    return payments.filter((payment) => {
      // Filter by year (null means show all years)
      if (filterYear !== null) {
        const paymentYear = new Date(payment.payment_date).getFullYear();
        if (paymentYear !== filterYear) return false;
      }

      // Filter by categories (multiple)
      if (filterCategories.length > 0 && !filterCategories.includes(payment.equipment_category)) {
        return false;
      }

      // Filter by purposes (multiple)
      if (filterPurposes.length > 0 && !filterPurposes.includes(payment.equipment_purpose)) {
        return false;
      }

      // Filter by equipment (multiple)
      if (filterEquipment.length > 0 && !filterEquipment.includes(payment.equipment_name)) {
        return false;
      }

      // Filter by creditors (multiple)
      if (filterCreditors.length > 0 && !filterCreditors.includes(payment.creditor)) {
        return false;
      }

      // Filter by payment frequencies (multiple)
      if (filterPaymentFrequencies.length > 0 && !filterPaymentFrequencies.includes(payment.payment_frequency)) {
        return false;
      }

      return true;
    });
  }, [
    payments,
    filterYear,
    filterCategories,
    filterPurposes,
    filterEquipment,
    filterCreditors,
    filterPaymentFrequencies,
  ]);

  // Notify parent component of filtered data changes
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredPayments);
    }
  }, [filteredPayments, onFilteredDataChange]);

  const columns = useMemo(
    () => [
      {
        field: "payment_date",
        headerName: "Payment Date",
        width: 120,
        valueFormatter: (value) => (value ? formatDate(value) : ""),
      },
      // Equipment Info Group (light green)
      {
        field: "equipment_name",
        headerName: "Equipment",
        width: 200,
        cellClassName: "grid-column-group-a first bold-header",
        headerClassName: "bold-header",
        renderCell: (params) => {
          const equipmentId = params.row.equipment_id;
          const equipmentName = params.value || "Unknown";

          if (!equipmentId || equipmentName === "Unknown") {
            return equipmentName;
          }

          return (
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/financial/equipment/${equipmentId}`);
              }}
              sx={{
                cursor: "pointer",
                textAlign: "left",
                fontWeight: "bold",
              }}
            >
              {equipmentName}
            </Link>
          );
        },
      },
      {
        field: "equipment_category",
        headerName: "Category",
        width: 130,
        cellClassName: "grid-column-group-a",
      },
      {
        field: "equipment_purpose",
        headerName: "Purpose",
        width: 150,
        cellClassName: "grid-column-group-a",
      },
      // Payment Details Group (light blue)
      {
        field: "payment_amount",
        headerName: "Payment",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
        cellClassName: "grid-column-group-b first bold-header",
        headerClassName: "bold-header",
      },
      {
        field: "principal_payment",
        headerName: "Principal",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
        cellClassName: "grid-column-group-b",
      },
      {
        field: "interest_payment",
        headerName: "Interest",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
        cellClassName: "grid-column-group-b",
      },
      {
        field: "extra_principal_payment",
        headerName: "Extra Principal",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatPrice(value || 0, 0),
        cellClassName: "grid-column-group-b",
      },
      // Balance Info
      {
        field: "beginning_balance",
        headerName: "Beginning Balance",
        width: 150,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
      },
      {
        field: "ending_balance",
        headerName: "Ending Balance",
        width: 150,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 150,
        flex: 1,
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md="auto">
          <Typography variant="h6">All Equipment Loan Payments</Typography>
        </Grid>

        {/* Filters */}
        <Grid item xs={12} md="auto">
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ width: "100%" }}
          >
            {/* Year Filter */}
            <Box sx={{ minWidth: 120 }}>
              <DropdownSelector
                value={filterYear}
                onChange={setFilterYear}
                options={getBudgetYears()}
                isRequired={false}
                clearLabel="All Years"
              />
            </Box>

            {/* Equipment Filter (Multiple) */}
            <Box sx={{ minWidth: 250 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableEquipment.map((eq) => eq.name)}
                value={filterEquipment}
                onChange={(event, newValue) => setFilterEquipment(newValue)}
                renderInput={(params) => <TextField {...params} label="Equipment" placeholder="All Equipment" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                limitTags={2}
              />
            </Box>

            {/* Category Filter (Multiple with chips) */}
            <Box sx={{ minWidth: 250 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableCategories}
                value={filterCategories}
                onChange={(event, newValue) => setFilterCategories(newValue)}
                renderInput={(params) => <TextField {...params} label="Category" placeholder="All Categories" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                limitTags={2}
              />
            </Box>

            {/* Purpose Filter (Multiple) */}
            <Box sx={{ minWidth: 250 }}>
              <Autocomplete
                multiple
                size="small"
                options={availablePurposes}
                value={filterPurposes}
                onChange={(event, newValue) => setFilterPurposes(newValue)}
                renderInput={(params) => <TextField {...params} label="Purpose" placeholder="All Purposes" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                limitTags={2}
              />
            </Box>

            {/* Creditor Filter (Multiple) */}
            <Box sx={{ minWidth: 250 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableCreditors}
                value={filterCreditors}
                onChange={(event, newValue) => setFilterCreditors(newValue)}
                renderInput={(params) => <TextField {...params} label="Creditor" placeholder="All Creditors" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                limitTags={2}
              />
            </Box>

            {/* Payment Frequency Filter (Multiple) */}
            <Box sx={{ minWidth: 250 }}>
              <Autocomplete
                multiple
                size="small"
                options={availablePaymentFrequencies}
                value={filterPaymentFrequencies}
                onChange={(event, newValue) => setFilterPaymentFrequencies(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Payment Frequency" placeholder="All Frequencies" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                limitTags={2}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <DataGrid
        rows={filteredPayments}
        columns={columns}
        getRowId={(row) => row.id}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: {
            sortModel: [{ field: "payment_date", sort: "asc" }],
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        filterMode="client"
        sx={{ width: "100%" }}
        autoHeight={true}
      />
    </Box>
  );
};
